// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md

import { getCatalogEntry } from '@greenlight/shared';

export type IntegrationBrand =
  // A bundled vendor mark. `darkSrc` is a dark-mode variant (e.g. a white-knockout
  // of a near-black mark like GitHub/OpenAI); when absent the single mark is used in
  // both themes (it has enough contrast on the neutral tile, ADR-0026 §6).
  | { kind: 'brand'; src: string; darkSrc?: string; label: string }
  // A brand-tinted monogram (deterministic hue from `seed`) for an entry with no
  // cleared asset — distinct and legible on both themes; never a broken image.
  | { kind: 'mono'; letter: string; label: string; seed: string }
  | { kind: 'generic'; label: string };

/** First alphanumeric char, for the monogram fallback. Hoisted so it compiles once. */
const FIRST_ALPHANUMERIC = /[a-z0-9]/i;

interface BrandAsset {
  light: string;
  /** Dark-mode variant; omit when the light mark already reads on both themes. */
  dark?: string;
}

/**
 * Catalog `logo_ref` → bundled brand asset (ADR-0026 §6). The single source of truth
 * for which marks have a cleared bundled asset; entries absent here fall back to a
 * monogram. Assets ship in `public/brands/` and never load from a vendor CDN. Marks
 * that are near-black (GitHub, OpenAI) carry a `dark` white-knockout so they stay
 * legible in dark mode; multi-/mid-tone marks read on both themes with one asset.
 */
const LOGO_ASSETS: Record<string, BrandAsset> = {
  github: { light: '/brands/github.svg', dark: '/brands/github-dark.svg' },
  openai: { light: '/brands/openai.svg', dark: '/brands/openai-dark.svg' },
  anthropic: { light: '/brands/anthropic.svg', dark: '/brands/anthropic-dark.svg' },
  notion: { light: '/brands/notion.svg', dark: '/brands/notion-dark.svg' },
  rippling: { light: '/brands/rippling.svg', dark: '/brands/rippling-dark.svg' },
  tableau: { light: '/brands/tableau.svg' },
  // Axiom's geometric A is near-black on light; the white knockout is the dark variant.
  axiom: { light: '/brands/axiom.svg', dark: '/brands/axiom-dark.svg' },
  slack: { light: '/brands/slack.svg' },
  'microsoft-teams': { light: '/brands/microsoft-teams.svg' },
  // Drawn in-repo (a node graph on Microsoft blue): Microsoft's own Graph mark is
  // not licensed for third-party use.
  'microsoft-graph': { light: '/brands/microsoft-graph.svg' },
  'google-workspace': { light: '/brands/google.svg' },
  zoom: { light: '/brands/zoom.svg' },
  salesforce: { light: '/brands/salesforce.svg' },
  snowflake: { light: '/brands/snowflake.svg' },
  close: { light: '/brands/close.png' },
  fireflies: { light: '/brands/fireflies.png' },
  hubspot: { light: '/brands/hubspot.svg' },
  outlook: { light: '/brands/outlook.svg' },
  // Market-data + niche vendors. The seals (SEC, FRED) and the gradient/green
  // marks read on both themes; Alpha Vantage's solid play-disc carries a lightened
  // dark variant like GitHub/OpenAI. DealCloud uses its parent Intapp's gradient
  // diamond — bright enough to read on both themes with one asset.
  'financial-modeling-prep': { light: '/brands/financial-modeling-prep.png' },
  'alpha-vantage': { light: '/brands/alpha-vantage.svg', dark: '/brands/alpha-vantage-dark.svg' },
  fred: { light: '/brands/fred.png' },
  earningscall: { light: '/brands/earningscall.png' },
  'sec-edgar': { light: '/brands/sec-edgar.png' },
  dealcloud: { light: '/brands/dealcloud.svg' },
  // NetSuite's mid-tone teal/gold swoosh reads on both themes with one asset.
  netsuite: { light: '/brands/netsuite.png' },
  // Microsoft's Fabric ribbon for the REST entry, and Fabric's own Data Warehouse
  // workload mark for the warehouse — two tiles a name apart otherwise, so they carry
  // the vendor's two distinct marks. Both mid-tone, so one asset each serves both themes.
  'microsoft-fabric': { light: '/brands/microsoft-fabric.png' },
  'fabric-warehouse': { light: '/brands/fabric-warehouse.png' },
  // Drawn in-repo (ascending bars on Power BI yellow): Microsoft's Power BI product
  // icon is not licensed for third-party use, and a third Fabric-family tile needs
  // its own mark to be told apart from the two above.
  'power-bi-semantic-models': { light: '/brands/power-bi-semantic-models.svg' },
  // Jira blue, Greenhouse green, Lattice's teal/yellow/red chevrons (cropped
  // from the vendor lockup), BigTime's two-blue B, Apollo's and DocuSign's
  // current app icons — all mid-/multi-tone, one asset each.
  jira: { light: '/brands/jira.svg' },
  greenhouse: { light: '/brands/greenhouse.svg' },
  lattice: { light: '/brands/lattice.svg' },
  bigtime: { light: '/brands/bigtime.png' },
  apollo: { light: '/brands/apollo.svg' },
  box: { light: '/brands/box.svg' },
  docusign: { light: '/brands/docusign.png' },
  // The USCIS seal, cropped out of the agency lockup. Its ring is opaque white, so
  // it reads on the dark card the same way the SEC and FRED seals do.
  'uscis-case-status': { light: '/brands/uscis-case-status.png' },
  // Platform-served User Directory — our own mark: a people glyph, not a vendor logo.
  'greenlight-directory': { light: '/brands/greenlight-directory.svg' },
};

// Legacy demo/Custom integration slugs that predate `catalog_key`, mapped to a
// `logo_ref` so they resolve through the same asset map (and get dark variants too).
const SLUG_TO_LOGO_REF: Record<string, string> = {
  github: 'github',
  // Upstream-vendor keys (docs/11 §Upstream status) resolve through the same
  // registry, so the health page's vendor tiles carry the real mark.
  anthropic: 'anthropic',
  openai: 'openai',
  'google-ai': 'google-workspace',
  slack: 'slack',
  'google-workspace': 'google-workspace',
  hubspot: 'hubspot',
  outlook: 'outlook',
  zoom: 'zoom',
  'salesforce-prod': 'salesforce',
  close: 'close',
  'snowflake-analytics': 'snowflake',
  fireflies: 'fireflies',
  fmp: 'financial-modeling-prep',
};

function brandFromAsset(asset: BrandAsset, label: string): IntegrationBrand {
  return { kind: 'brand', src: asset.light, darkSrc: asset.dark, label };
}

// Monogram from the first alphanumeric of the label (brand-tinted via `seed`), or a
// generic glyph tile when none can be extracted. The shared fallback below.
function monogramOrGeneric(label: string, seed: string): IntegrationBrand {
  const firstAlphanumeric = label.match(FIRST_ALPHANUMERIC);
  if (firstAlphanumeric !== null && firstAlphanumeric[0] !== undefined) {
    return { kind: 'mono', letter: firstAlphanumeric[0].toUpperCase(), label, seed };
  }
  return { kind: 'generic', label };
}

/**
 * Resolve a tile descriptor from the integration's `catalog_key` (ADR-0026 §6): the
 * entry's `logo_ref` → a bundled asset, else a brand-tinted monogram. The primary
 * path for a catalogued integration.
 */
export function brandForCatalogKey(catalogKey: string, displayName?: string): IntegrationBrand {
  const entry = getCatalogEntry(catalogKey);
  const label = displayName ?? entry?.displayName ?? catalogKey;
  const asset = entry ? LOGO_ASSETS[entry.logoRef] : undefined;
  return asset ? brandFromAsset(asset, label) : monogramOrGeneric(label, catalogKey);
}

/**
 * Resolve a tile descriptor from an integration slug — the fallback for Custom
 * (catalog-less) and legacy demo integrations. Maps known slugs to a bundled asset,
 * else a brand-tinted monogram from `displayName ?? slug`.
 */
export function brandForSlug(slug: string, displayName?: string): IntegrationBrand {
  const logoRef = SLUG_TO_LOGO_REF[slug];
  const asset = logoRef ? LOGO_ASSETS[logoRef] : undefined;
  return asset
    ? brandFromAsset(asset, displayName ?? slug)
    : monogramOrGeneric(displayName ?? slug, slug);
}
